import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import {
  AlertService,
  Chain,
  ChainRepository,
  SignerService,
  TenderlySimulation,
  TenderlySimulationFactory,
  TenderlySimulationRequestDto,
  TenderlySimulationResponseDto,
  TenderlyStateObjectsDto,
} from 'common';
import { BytesLike, concat, keccak256, zeroPadValue } from 'ethers';
import { Address, AddressConstants } from '@31third/common';
import { Rebalancing } from '../model';
import { APP_CONFIG } from 'app-config';
import { EnzymeRepository } from '../repository';

@Injectable({
  providedIn: 'root',
})
export class TenderlyService {
  private static readonly GENERIC_ERROR_MESSAGE =
    'Simulation currently not possible';

  constructor(
    @Inject(APP_CONFIG) private appConfig: any,
    private readonly signerService: SignerService,
    private readonly chainRepository: ChainRepository,
    private readonly enzymeRepository: EnzymeRepository,
    private readonly httpClient: HttpClient,
    private readonly alertService: AlertService,
  ) {}

  public async simulateRebalancing(
    rebalancing: Rebalancing,
    stateObjects: TenderlyStateObjectsDto = {},
    awaitPublication = false,
  ): Promise<TenderlySimulation> {
    const signerAddress = await this.getSignerAddress();
    const chain = this.getChain();

    if (!rebalancing.txHandler || !rebalancing.txData) {
      throw new Error(
        'Just basket trades can be simulated (activate in settings)',
      );
    }

    try {
      const simulationResponse = await lastValueFrom(
        this.httpClient.post<TenderlySimulationResponseDto>(
          `https://api.tenderly.co/api/v1/account/${this.appConfig.tenderlyAccount}/project/${this.appConfig.tenderlyProject}/simulate`,
          // the transaction
          {
            save: true,
            save_if_fails: true,
            simulation_type: 'quick',

            network_id: chain.getIdentifierAsDecimal().toString(),

            from: signerAddress,
            to: rebalancing.txHandler,
            input: rebalancing.txData,
            value: rebalancing.txValue ? rebalancing.txValue.toString() : 0,

            estimate_gas: true,
            state_objects: stateObjects,
          } as TenderlySimulationRequestDto,
          {
            headers: {
              'Content-Type': 'application/json',
              'X-Access-Key': this.appConfig.tenderlyAccessKey,
            },
          },
        ),
      );

      const tenderlySimulation =
        TenderlySimulationFactory.createFromTenderlyDto(
          simulationResponse,
          this.appConfig.tenderlyAccount,
          this.appConfig.tenderlyProject,
        );

      if (tenderlySimulation.id) {
        // make simulation public
        const publicationPromise = lastValueFrom(
          this.httpClient.post(
            `https://api.tenderly.co/api/v1/account/${this.appConfig.tenderlyAccount}/project/${this.appConfig.tenderlyProject}/simulations/${tenderlySimulation.id}/share`,
            {},
            {
              headers: {
                'Content-Type': 'application/json',
                'X-Access-Key': this.appConfig.tenderlyAccessKey,
              },
            },
          ),
        );
        if (awaitPublication) {
          await publicationPromise;
        }
      }

      return tenderlySimulation;
    } catch (e: any) {
      console.error(e);
      if (e.statusText === 'OK') {
        throw new Error(e.error?.error?.message);
      }
      throw new Error(TenderlyService.GENERIC_ERROR_MESSAGE);
    }
  }

  private getChain(): Chain {
    const chain = this.chainRepository.store.getValue().chain;
    if (!chain) {
      throw new Error('No chain selected');
    }
    return chain;
  }

  private async getSignerAddress(): Promise<Address> {
    let signerAddress: Address | undefined;
    if (
      !this.enzymeRepository.store.getValue().selectedVault ||
      !this.enzymeRepository.store.getValue().useOwnerInsteadOfConnectedWallet
    ) {
      signerAddress = await this.signerService.getSigner()?.getAddress();
    } else {
      signerAddress =
        this.enzymeRepository.store.getValue().selectedVault?.owner;
    }
    if (!signerAddress) {
      throw new Error('Wallet not connected anymore');
    }
    return signerAddress;
  }

  public async simulateRebalancingAndOpenLink(
    rebalancing: Rebalancing,
    overrideBalancesAndAllowances: boolean,
    useZeroGasPrice: boolean,
  ): Promise<void> {
    this.alertService.showInfo('Simulation runs and will open in new tab');

    const fromAmountMap: Map<Address, bigint> = new Map<Address, bigint>();
    rebalancing.trades?.forEach(trade => {
      if (!fromAmountMap.has(trade.from.address)) {
        fromAmountMap.set(trade.from.address, 0n);
      }
      fromAmountMap.set(
        trade.from.address,
        fromAmountMap.get(trade.from.address)! + trade.fromAmount,
      );
    });

    const owner = await this.getSignerAddress();
    const spender = rebalancing.txHandler!;

    // NOTE: can be used to find correct slot
    // Just set state overrides on Tenderly and compare hash to log output
    // console.log('### START SLOT FINDING ###');
    // for (let i = 0; i < 20; i++) {
    //   console.log(`Slot#${i}`);
    //   console.log(
    //     `balance: ${this.getMappingSlot(
    //       BigInt(i).toHexString(),
    //       owner,
    //     )}`,
    //   );
    //   console.log(
    //     `allowance: ${this.getNestedMappingSlot(
    //       BigInt(i).toHexString(),
    //       owner,
    //       spender,
    //     )}`,
    //   );
    // }
    // console.log('### END SLOT FINDING ###');

    const stateObjects: TenderlyStateObjectsDto = {};
    if (overrideBalancesAndAllowances) {
      [...fromAmountMap.keys()].forEach(address => {
        const tokenSlots = this.getTokenSlots(address);

        if (tokenSlots) {
          const balanceSlotHash = this.getMappingSlot(
            tokenSlots.balanceSlot,
            owner,
          );
          const allowanceSlotHash = this.getNestedMappingSlot(
            tokenSlots.allowanceSlot,
            owner,
            spender,
          );

          const amount = (fromAmountMap.get(address)! * 110n) / 100n; // add 10% buffer

          if (!stateObjects[`${address}`]) {
            stateObjects[`${address}`] = { storage: {} };
          }

          if (!stateObjects[`${address}`]['storage'][`${balanceSlotHash}`]) {
            stateObjects[`${address}`]['storage'][`${balanceSlotHash}`] =
              `${zeroPadValue(amount.toString(16), 32)}`;
          }

          if (!stateObjects[`${address}`]['storage'][`${allowanceSlotHash}`]) {
            stateObjects[`${address}`]['storage'][`${allowanceSlotHash}`] =
              `${zeroPadValue(amount.toString(16), 32)}`;
          }
        }
      });
    }

    try {
      const result = await this.simulateRebalancing(
        rebalancing,
        stateObjects,
        true,
      );

      if (result.id) {
        window.open(result.getPublicUrl(), '_blank');
      } else {
        this.alertService.showError(TenderlyService.GENERIC_ERROR_MESSAGE);
      }
    } catch (e) {
      if (typeof e === 'string') {
        this.alertService.showError(e);
      } else {
        this.alertService.showError(TenderlyService.GENERIC_ERROR_MESSAGE);
      }
    }
  }

  private getMappingSlot(slot: BytesLike, key: BytesLike): string {
    const paddedSlot = zeroPadValue(slot, 32);
    const paddedKey = zeroPadValue(key, 32);

    return keccak256(concat([paddedKey, paddedSlot]));
  }

  private getNestedMappingSlot(
    slot: BytesLike,
    key: BytesLike,
    innerKey: BytesLike,
  ): string {
    const firstLevelSlotHash = this.getMappingSlot(slot, key);
    return this.getMappingSlot(firstLevelSlotHash, innerKey);
  }

  private getTokenSlots(address: Address): TokenSlots | undefined {
    switch (address.toLowerCase()) {
      case AddressConstants.ETHEREUM.USDT_ADDRESS.toLowerCase():
        return {
          balanceSlot: '0x2',
          allowanceSlot: '0x5',
        };
      case AddressConstants.ETHEREUM.USDC_ADDRESS.toLowerCase():
        return {
          balanceSlot: '0x9', // slot 9
          allowanceSlot: '0xa', // slot 10
        };
      case AddressConstants.ETHEREUM.LINK_ADDRESS.toLowerCase():
        return {
          balanceSlot: '0x1',
          allowanceSlot: '0x2',
        };
      case AddressConstants.ETHEREUM.MKR_ADDRESS.toLowerCase():
        return {
          balanceSlot: '0x1',
          allowanceSlot: '0x2',
        };
      case AddressConstants.ETHEREUM.UNI_ADDRESS.toLowerCase():
        return {
          balanceSlot: '0x4',
          allowanceSlot: '0x3',
        };
      case AddressConstants.ETHEREUM.AAVE_ADDRESS.toLowerCase():
        return {
          balanceSlot: '0x0',
          allowanceSlot: '0x1',
        };
      case AddressConstants.ETHEREUM.SHIB_ADDRESS.toLowerCase():
        return {
          balanceSlot: '0x0',
          allowanceSlot: '0x1',
        };
      case AddressConstants.ETHEREUM.MATIC_ADDRESS.toLowerCase():
        return {
          balanceSlot: '0x0',
          allowanceSlot: '0x1',
        };
      case AddressConstants.ETHEREUM.SAND_ADDRESS.toLowerCase():
        return {
          balanceSlot: '0x5',
          allowanceSlot: '0x6',
        };
      default:
        this.alertService.showError(
          `Simulation for ${address} currently not supported`,
        );
    }
    return undefined;
  }
}

interface TokenSlots {
  balanceSlot: string;
  allowanceSlot: string;
}
